//================================================================
//  Page: ApproversM
//================================================================

//  Purpose: My Approvers page (Mobile Version) - shows all approver types and history for the current user

//================================================================

//Libraries
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import ApproverCard from './ApproverCard';

//Functions
import QueryListener from '../../../Library/QueryListener';

//Images
import ArrowIcon from '../../../Components/Images/Icon_LeftArrow_Black.svg';
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';


export default function ApproversM() {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const navigate = useNavigate();
    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates & useReducer
    //------------------------------------------------------

    // Used to manage the page status
    const [pageStatus, setPageStatus] = useState('pending');

    // Used to manage the current tab view
    const [currentView, setCurrentView] = useState(

        // Filter by Admin type (Demand vs Commercial)
        // -- Note: Assumed people WON'T have both roles at ONCE!!!
        getUser?.roles?.demandManagementAdmin === true ? 'Initial Approvers' : 'Budget Owners'

    );

    // Used to manage the search input
    const [searchInput, setSearchInput] = useState('');

    // Used to store the approvers, history and filtered data
    const [cards, setCards] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'approvers': [],
            'history': [],
            'filteredData': []
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function to handle search input
    function handleSearch(value) {

        setSearchInput(value);

        const currentData = currentView === 'History' ? cards.history : cards.approvers;

        if (value.length === 0) {
            setCards({ filteredData: currentData });
            return;
        }

        const searchResults = currentData.filter((object) =>
            currentView === 'History'
                ?
                object?.previousapprover?.email?.toLowerCase().includes(value.toLowerCase()) ||
                object?.actionedby?.email?.toLowerCase().includes(value.toLowerCase()) ||
                object?.newapprover?.email?.toLowerCase().includes(value.toLowerCase()) ||
                object?.role?.toLowerCase().includes(value.toLowerCase())
                :
                object?.approveremail?.toLowerCase().includes(value.toLowerCase()) ||
                object?.lastmodifiedby?.email?.toLowerCase().includes(value.toLowerCase()) ||
                object?.role?.toLowerCase().includes(value.toLowerCase())
        );

        setCards({ filteredData: searchResults });

    }

    //--------------------------------------------------
    //  useEffects
    //--------------------------------------------------

    // Get card data
    useEffect(() => {

        if (getUser === undefined) return;

        let unsubscribe;

        function onLoadChange(documents) {
            
            if (currentView === 'History') {

                setCards({
                    'history': documents,
                    'filteredData': documents
                });

            } else {

                setCards({
                    'approvers': documents,
                    'filteredData': documents
                });

            }
            
            setPageStatus('onload');
        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        if (currentView === 'History') {

            // Filter by Admin type (Demand vs Commercial)
            // -- Note: Assumed people WON'T have both roles at ONCE!!!
            const query = getUser?.roles?.demandManagementAdmin === true ?  
                ['approvalbucket', 'not-in', ['Budget Owner (LoA) Approval', 'Finance Manager Approval']] 
                : 
                ['approvalbucket', 'in', ['Budget Owner (LoA) Approval', 'Finance Manager Approval']] 
                
            unsubscribe = QueryListener('approverhistory', [
                query
            ], onLoadChange, onLoadChange, onError);

        } else {


            // Filter by Admin type (Demand vs Commercial)
            // -- Note: Assumed people WON'T have both roles at ONCE!!!
            const queryConfig = getUser?.roles?.demandManagementAdmin === true ? 
                {
                    'Initial Approvers': ['approvalbucket', '==', 'Initial Approval'],
                    'Final Approvers': ['approvalbucket', '==', 'Final Approval'],
                    'LoA Approvers': ['approvalbucket', '==', 'LoA Approval'],
                    'COO': ['approvalbucket', '==', 'COO Approval']
                }
                :
                {
                    'Budget Owners': ['approvalbucket', '==', 'Budget Owner (LoA) Approval'],
                    'Finance Manager': ['approvalbucket', '==', 'Finance Manager Approval'],
                }

            if (queryConfig[currentView]) {
                unsubscribe = QueryListener('approvers', [queryConfig[currentView]], onLoadChange, onLoadChange, onError);
            }

        }

        return () => {
            unsubscribe();
        };

    }, [currentView, getUser]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------
    
    return (
        <PageComponent
            status={pageStatus}
            header={
                <div className='flex flex-col'>

                    {/* Header Bar with Back Button */}
                    <div className='flex flex-row bg-[#E3F1FB] w-full p-3 items-center'>
                        <img className='h-6' src={ArrowIcon} alt='Back' onClick={() => navigate('/account')}></img>
                        <h4 className='flex items-center h-full cursor-default m-auto text-[22px]'>
                            Approvers
                        </h4>
                    </div>

                    {/* View Selector */}
                    <div className=' bg-neutral-100 h-16 px-4 py-[12px]'>
                        <select
                            className='text-[15px] text-gray-900 h-[45px] w-full rounded-md border border-gray-300 p-2.5 font-normal'
                            value={currentView}
                            onChange={(e) => { 
                                setCurrentView(e.target.value); 
                                setSearchInput('') 
                            }}
                        >
                            {
                                getUser?.roles?.demandManagementAdmin === true ? 
                                <>
                                    <option value='Initial Approvers'>Initial Approvers</option>
                                    <option value='Final Approvers'>Final Approvers</option>
                                    <option value='LoA Approvers'>LoA Approvers</option>
                                    <option value='COO'>COO</option>
                                    <option value='History'>History</option>
                                </>
                                :
                                <>
                                    <option value='Budget Owners'>Budget Owners</option>
                                    <option value='Finance Manager'>Finance Manager</option>
                                    <option value='History'>History</option>
                                </>
                            }

                        </select>
                    </div>
                </div>
            }
            body={
                <div className='flex flex-col'>

                    {/* Search Bar */}
                    <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] mx-4 mt-4 mb-0 rounded-[5px] border-1 border-solid border-[#dee2e6]'>
                        <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                        <label htmlFor='searchInput'>
                            <input
                                id='searchInput'
                                className='border-none h-[37px] p-0 m-0 outline-none bg-white'
                                type='text'
                                placeholder='Search approvers'
                                onChange={(e) => handleSearch(e.target.value)}
                                value={searchInput}
                                autoComplete='off'
                            ></input>
                        </label>
                    </div>

                    {/* Approvers Cards */}
                    <div className='px-4 pt-4'>
                        {
                            cards.filteredData.map((approver, index) =>
                                <div key={index}> 
                                    <ApproverCard
                                        approver={approver}
                                        currentView={currentView}
                                    ></ApproverCard>
                                </div>
                            )
                        }
                    </div>
                </div>
            }
        ></PageComponent>
    );
}